import { CADENCES, CADENCE_LABEL, CADENCE_NOUN, CADENCE_RANK, type Cadence } from './types';

export interface FormulaMetric {
  title: string;
  cadence: Cadence;
}

export const FORMULA_FUNCTIONS: { name: string; description: string; result: string }[] = [
  { name: 'SUM', description: 'Total across periods', result: 'Total' },
  { name: 'MEAN', description: 'Average across periods', result: 'Average' },
  { name: 'MEDIAN', description: 'Middle value across periods', result: 'Middle value' },
  { name: 'MIN', description: 'Lowest value across periods', result: 'Lowest' },
  { name: 'MAX', description: 'Highest value across periods', result: 'Highest' },
  { name: 'LAST', description: 'Value in the final shorter period', result: 'Final value' },
  { name: 'PREV', description: 'Value from the previous period', result: 'Value' },
];

/** Where a metric sits in a formula: used directly, inside PREV, or inside an aggregate function. */
export type ReferenceContext = { kind: 'direct' } | { kind: 'previous' } | { kind: 'aggregate'; name: string };

const EXAMPLE_PERIODS: Record<Cadence, number> = { weekly: 4, monthly: 3, quarterly: 4, yearly: 2 };

const adjective = (cadence: Cadence) => CADENCE_LABEL[cadence].toLowerCase();
const withArticle = (word: string) => `${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}`;
const periods = (cadence: Cadence) => `${CADENCE_NOUN[cadence]}s`;
const eachPeriodOf = (formulaCadence: Cadence) => `each ${CADENCE_NOUN[formulaCadence]}'s`;
const isLonger = (cadence: Cadence, than: Cadence) => CADENCE_RANK[cadence] > CADENCE_RANK[than];
const shorterThan = (cadence: Cadence) => CADENCES.filter((candidate) => CADENCE_RANK[candidate] < CADENCE_RANK[cadence]);
const isLast = (context: ReferenceContext) => context.kind === 'aggregate' && context.name === 'LAST';

function joinWithOr(words: string[]): string {
  if (words.length <= 2) return words.join(' or ');
  return `${words.slice(0, -1).join(', ')}, or ${words[words.length - 1]}`;
}

/** Why a metric cannot appear in this context; null when it can. */
export function referenceProblem(
  metric: FormulaMetric, context: ReferenceContext, formulaCadence: Cadence, noun: string,
): string | null {
  const { title, cadence } = metric;
  if (cadence === formulaCadence) {
    return isLast(context)
      ? `LAST needs a ${noun} with a shorter cadence, but "${title}" is ${adjective(cadence)} like this formula.`
      : null;
  }
  if (isLonger(cadence, formulaCadence)) {
    return `"${title}" is ${adjective(cadence)}, a longer cadence than this ${adjective(formulaCadence)} formula, so it can't be used here.`;
  }
  if (context.kind === 'aggregate') return null;
  if (context.kind === 'previous') {
    return `PREV needs ${withArticle(adjective(formulaCadence))} ${noun}, but "${title}" is ${adjective(cadence)}.`;
  }
  return `"${title}" is ${adjective(cadence)}, but this formula is ${adjective(formulaCadence)}. Wrap it in a function, like SUM(${title}).`;
}

/** The same rule as `referenceProblem`, short enough for a suggestion byline. */
export function referenceUnavailableReason(
  metric: FormulaMetric, context: ReferenceContext, formulaCadence: Cadence,
): string | null {
  const { cadence } = metric;
  if (cadence === formulaCadence) return isLast(context) ? 'LAST needs shorter' : null;
  if (isLonger(cadence, formulaCadence)) return 'Longer than this formula';
  if (context.kind === 'aggregate') return null;
  if (context.kind === 'previous') return `PREV needs ${adjective(formulaCadence)}`;
  return 'Wrap in a function';
}

/**
 * Whether an aggregate function's trailing-periods argument fits the cadences
 * involved. `periodsText` is the argument as written: undefined when there is
 * no comma, empty when a comma has no number after it yet.
 */
export function trailingPeriodsProblem(
  name: string, metric: FormulaMetric, periodsText: string | undefined, formulaCadence: Cadence,
): string | null {
  const { title, cadence } = metric;
  if (isLonger(cadence, formulaCadence)) return null;
  if (cadence === formulaCadence && periodsText === undefined) {
    return `"${title}" is ${adjective(cadence)} like this formula, so ${name} needs a number of trailing periods, like ${name}(${title}, ${EXAMPLE_PERIODS[cadence]}).`;
  }
  if (cadence !== formulaCadence && periodsText !== undefined) {
    const extra = periodsText ? `the ${periodsText}` : 'the comma';
    return `Remove ${extra} from ${name}: "${title}" is ${adjective(cadence)}, so ${eachPeriodOf(formulaCadence)} ${periods(cadence)} are already combined.`;
  }
  return null;
}

/** The standing tip below the formula field. It depends only on the formula's cadence. */
export function tipLines(formulaCadence: Cadence, pluralNoun: string): string[] {
  const shorter = shorterThan(formulaCadence);
  const combined = shorter.length === 1 ? periods(shorter[0]) : 'periods';
  const wrap = shorter.length
    ? `, or wrap ${joinWithOr(shorter.map(adjective))} in a function to combine ${eachPeriodOf(formulaCadence)} ${combined}`
    : '';
  return [
    `Use ${adjective(formulaCadence)} ${pluralNoun}${wrap}.`,
    `Functions: ${FORMULA_FUNCTIONS.map((fn) => fn.name).join(' · ')}`,
  ];
}

/** Whether the trailing-periods argument is shown as required, optional, or absent. */
export type PeriodsShape = 'required' | 'optional' | 'none';

export interface FunctionHint {
  periods: PeriodsShape;
  byline: string;
}

/** The argument hint for a function call, given what has been written inside it. */
export function functionHint(
  name: string,
  call: { metric?: FormulaMetric; isSelf?: boolean; argument: number; periodsText?: string },
  formulaCadence: Cadence,
  noun: string,
): FunctionHint | null {
  const fn = FORMULA_FUNCTIONS.find((candidate) => candidate.name === name);
  if (!fn) return null;
  const { metric, isSelf, argument, periodsText } = call;
  if (name === 'PREV') {
    if (isSelf) {
      const period = CADENCE_NOUN[formulaCadence];
      return { periods: 'none', byline: `This ${noun}'s latest value before the current ${period}, or its opening value in its start ${period}.` };
    }
    const problem = metric && referenceProblem(metric, { kind: 'previous' }, formulaCadence, noun);
    return { periods: 'none', byline: problem || `Value from the previous ${CADENCE_NOUN[formulaCadence]}.` };
  }
  if (name === 'LAST') {
    if (!metric) {
      return {
        periods: 'none',
        byline: shorterThan(formulaCadence).length
          ? `Value in the final shorter period of each ${CADENCE_NOUN[formulaCadence]}.`
          : `LAST needs a ${noun} with a shorter cadence than this ${adjective(formulaCadence)} formula.`,
      };
    }
    const problem = referenceProblem(metric, { kind: 'aggregate', name }, formulaCadence, noun);
    return {
      periods: 'none',
      byline: problem ?? `"${metric.title}" in the final ${CADENCE_NOUN[metric.cadence]} of each ${CADENCE_NOUN[formulaCadence]}.`,
    };
  }
  if (!metric) {
    if (!shorterThan(formulaCadence).length) {
      return { periods: 'required', byline: `${fn.result} of the trailing ${periods(formulaCadence)}.` };
    }
    return {
      periods: 'optional',
      byline: `${fn.result} of trailing ${periods(formulaCadence)}, or of ${eachPeriodOf(formulaCadence)} shorter periods.`,
    };
  }
  if (isLonger(metric.cadence, formulaCadence)) {
    return { periods: 'optional', byline: referenceProblem(metric, { kind: 'aggregate', name }, formulaCadence, noun)! };
  }
  if (metric.cadence === formulaCadence) {
    return {
      periods: 'required',
      byline: argument === 0
        ? `${fn.result} of "${metric.title}" over trailing ${periods(formulaCadence)}.`
        : `How many ${periods(formulaCadence)}, ending with the current one.`,
    };
  }
  return {
    periods: 'none',
    byline: argument === 0
      ? `${fn.result} of "${metric.title}" across ${eachPeriodOf(formulaCadence)} ${periods(metric.cadence)}.`
      : trailingPeriodsProblem(name, metric, periodsText ?? '', formulaCadence)!,
  };
}
